import * as React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogHeading, Typography } from 'omakase';
import { Card, CardContent, LoadingContainer } from 'ui';
import { StoreType } from '@tock/shared/utils/connect';
import { getSettings } from 'settings/AppSettings';
import { makeStyles } from '@material-ui/core/styles';
import CreditCardForm from '@tock/shared/forms/credit-card/CreditCardForm';
import Fieldset from '@tock/shared/components/forms/Fieldset';
import Form from '@tock/shared/components/forms/Form';
import PaymentScriptLoader from '@tock/shared/components/render-prop-components/PaymentScriptLoader';

const useStyles = makeStyles((theme) => ({
  header: {
    marginTop: theme.spacing(2),
  },
  cardRoot: {
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(-3),
      marginRight: theme.spacing(-3),
    },
    marginBottom: theme.spacing(1.5),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    },
  },
  dialogContent: {
    backgroundColor: theme.colors.bgDefault,
    paddingTop: 0,
    paddingBottom: 0,
  },
  actions: {
    flexDirection: 'column-reverse',
    '& button': {
      marginTop: theme.spacing(1),
      marginLeft: 0,
      '&:last-child': {
        marginLeft: 0,
        marginTop: 0,
      },
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      '& button': {
        marginRight: theme.spacing(1),
        marginTop: 0,
        '&:last-child': {
          marginTop: 0,
          marginRight: 0,
        },
      },
    },
  },
}));

interface Props {
  onSubmit(): void;
  submitting: boolean;
  paymentCard: StoreType['paymentCard'];
  fields: {
    cvc: ReduxFormField<string>;
    expiry: ReduxFormField<string>;
    name: ReduxFormField<string>;
    number: ReduxFormField<string>;
    zipCode: ReduxFormField<string>;
    skipAvsCheck?: ReduxFormField<boolean>;
  };
  error?: string;
}

const PaymentCardDialog: React.FunctionComponent<Props> = ({
  onSubmit,
  paymentCard,
  fields,
  submitting,
  error,
}) => {
  const classes = useStyles();
  return (
    <PaymentScriptLoader
      deviceData={paymentCard.deviceData}
      stripePublicKey={getSettings(__ENV__).STRIPE_PUBLIC_KEY}
    >
      <Dialog
        variant="partial"
        aria-labelledby="payment-card-dialog"
        open={true}
        disableBackdropClick
      >
        <DialogHeading id="payment-card-dialog">Please add a payment method</DialogHeading>
        <DialogContent className={classes.dialogContent}>
          <LoadingContainer loading={submitting} showChildrenWhileLoading={true}>
            <div>
              <Typography variant="body02" component="div" mb={2}>
                As you begin your order, please add a payment method. Your card will not be charged
                until your experience is complete.
              </Typography>
            </div>
            <Card className={classes.cardRoot} elevation={0}>
              <CardContent className={classes.cardContent}>
                <Form modifierClassName="Form--default Form--card">
                  <Fieldset>
                    {error && (
                      <div className="Errors Errors--form">
                        <div className="Error">{error}</div>
                      </div>
                    )}

                    <CreditCardForm autoComplete={true} fields={fields} />
                  </Fieldset>
                </Form>
              </CardContent>
            </Card>
          </LoadingContainer>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button variant="primary" size="large" disabled={submitting} fullWidth onClick={onSubmit}>
            Add payment method
          </Button>
        </DialogActions>
      </Dialog>
    </PaymentScriptLoader>
  );
};

export default PaymentCardDialog;
